/**
 * Map Store - manages the current map document and editing operations
 */

import { defineStore } from 'pinia';
import { ref, computed, shallowRef, triggerRef } from 'vue';
import {
    MapDocument,
    TileData,
    TileFlagKey,
    createEmptyMap,
    getPatternFrame,
    TERRAIN_PRESETS,
} from '@/formats/types';
import { loadAmdFile, serializeAmd, downloadAmd } from '@/formats/amd';
import { useAssetsStore } from '@/stores/assets';

/** Undo/redo operation */
interface MapOperation {
    /** Tiles before the operation */
    before: Map<string, TileData>;
    /** Tiles after the operation */
    after: Map<string, TileData>;
}

/** Called for every tile whose data changed (edits, undo, redo) */
export type TileChangeListener = (x: number, y: number, tile: TileData) => void;

/** Maximum undo history size */
const MAX_UNDO_HISTORY = 50;

// File System Access API - not in TypeScript's DOM lib yet
interface FilePickerOptions {
    suggestedName?: string;
    types?: Array<{ description: string; accept: Record<string, string[]> }>;
}

interface FilePickerWindow {
    showOpenFilePicker?: (options?: FilePickerOptions) => Promise<FileSystemFileHandle[]>;
    showSaveFilePicker?: (options?: FilePickerOptions) => Promise<FileSystemFileHandle>;
}

const AMD_PICKER_TYPES = [
    { description: 'Helbreath map', accept: { 'application/octet-stream': ['.amd'] } },
];

function isAbortError(err: unknown): boolean {
    return err instanceof DOMException && err.name === 'AbortError';
}

export const useMapStore = defineStore('map', () => {
    // State
    // The document ref is only replaced when a different map is opened. Edits
    // mutate the tile arrays in place and call triggerRef, so watching
    // `document` means "a new map was loaded", not "a tile changed".
    const document = shallowRef<MapDocument | null>(null);
    // Shallow: the snapshots hold the map's own tile objects, which must not
    // come back wrapped in reactive proxies when an operation is re-applied
    const undoStack = shallowRef<MapOperation[]>([]);
    const redoStack = shallowRef<MapOperation[]>([]);
    const lastError = ref<string | null>(null);

    // Not reactive: written on every tile edit
    let currentOperation: MapOperation | null = null;
    let fileHandle: FileSystemFileHandle | null = null;
    let triggerScheduled = false;
    const tileChangeListeners = new Set<TileChangeListener>();

    // Computed
    const hasMap = computed(() => document.value !== null);
    const isDirty = computed(() => document.value?.dirty ?? false);
    const canUndo = computed(() => undoStack.value.length > 0);
    const canRedo = computed(() => redoStack.value.length > 0);
    const mapWidth = computed(() => document.value?.width ?? 0);
    const mapHeight = computed(() => document.value?.height ?? 0);
    const filename = computed(() => document.value?.filename ?? 'untitled.amd');

    // Actions

    /**
     * Subscribe to tile changes. Returns the unsubscribe function.
     */
    function onTileChanged(listener: TileChangeListener): () => void {
        tileChangeListeners.add(listener);
        return () => tileChangeListeners.delete(listener);
    }

    /**
     * Tell Vue the document changed. Coalesced per microtask because a single
     * fill or brush stroke edits thousands of tiles.
     */
    function scheduleDocumentTrigger(): void {
        if (triggerScheduled) return;
        triggerScheduled = true;
        queueMicrotask(() => {
            triggerScheduled = false;
            triggerRef(document);
        });
    }

    function notifyTileChanged(x: number, y: number, tile: TileData): void {
        for (const listener of tileChangeListeners) {
            listener(x, y, tile);
        }
    }

    /**
     * Get flat array of tiles for sprite loading
     */
    function getFlatTiles(map: MapDocument): Array<{ tileSprite: number; objectSprite: number }> {
        const flat: Array<{ tileSprite: number; objectSprite: number }> = [];
        for (let x = 0; x < map.width; x++) {
            for (let y = 0; y < map.height; y++) {
                flat.push(map.tiles[x][y]);
            }
        }
        return flat;
    }

    /**
     * Replace the open document and load the sprites it needs
     */
    async function openDocument(map: MapDocument, handle: FileSystemFileHandle | null): Promise<void> {
        const assetsStore = useAssetsStore();

        document.value = map;
        fileHandle = handle;
        undoStack.value = [];
        redoStack.value = [];
        currentOperation = null;
        lastError.value = null;

        await assetsStore.loadSpritesForMap(getFlatTiles(map));
    }

    /**
     * Create a new empty map
     */
    async function newMap(width: number, height: number, terrainIndex: number = 0): Promise<void> {
        const map = createEmptyMap(width, height);
        const preset = TERRAIN_PRESETS[terrainIndex] ?? TERRAIN_PRESETS[0];

        // Fill with terrain preset
        for (let x = 0; x < width; x++) {
            for (let y = 0; y < height; y++) {
                map.tiles[x][y].tileSprite = preset.pattern.tileSprite;
                map.tiles[x][y].tileSpriteFrame = getPatternFrame(preset.pattern, x, y);
            }
        }

        await openDocument(map, null);
    }

    /**
     * Load a map from a File. Failures are reported through lastError.
     */
    async function loadMap(file: File, handle: FileSystemFileHandle | null = null): Promise<boolean> {
        try {
            const map = await loadAmdFile(file);
            await openDocument(map, handle);
            return true;
        } catch (err) {
            lastError.value = `Could not open ${file.name}: ${err instanceof Error ? err.message : String(err)}`;
            console.error('Map loading error:', err);
            return false;
        }
    }

    /**
     * Ask the user for a map file. Uses the File System Access API where
     * available so the map can later be saved back in place.
     */
    async function openMapWithPicker(): Promise<void> {
        // Pickers must be called as window methods ("Illegal invocation" otherwise)
        const pickerWindow = window as FilePickerWindow;

        if (pickerWindow.showOpenFilePicker) {
            try {
                const [handle] = await pickerWindow.showOpenFilePicker({ types: AMD_PICKER_TYPES });
                await loadMap(await handle.getFile(), handle);
            } catch (err) {
                if (!isAbortError(err)) {
                    lastError.value = `Could not open file: ${err instanceof Error ? err.message : String(err)}`;
                }
            }
            return;
        }

        const input = window.document.createElement('input');
        input.type = 'file';
        input.accept = '.amd';
        input.onchange = () => {
            const file = input.files?.[0];
            if (file) {
                void loadMap(file);
            }
        };
        input.click();
    }

    function markSaved(newFilename?: string): void {
        if (!document.value) return;
        document.value.dirty = false;
        if (newFilename) {
            document.value.filename = newFilename;
        }
        triggerRef(document);
    }

    /**
     * Save the current map. Writes back to the opened file when the browser
     * allows it, otherwise downloads. `saveAs` always asks for a location.
     */
    async function saveMap(saveAs: boolean = false): Promise<void> {
        if (!document.value) return;

        const pickerWindow = window as FilePickerWindow;

        try {
            let handle = saveAs ? null : fileHandle;

            if (!handle && pickerWindow.showSaveFilePicker) {
                handle = await pickerWindow.showSaveFilePicker({ suggestedName: filename.value, types: AMD_PICKER_TYPES });
            }

            if (handle) {
                const writable = await handle.createWritable();
                await writable.write(serializeAmd(document.value));
                await writable.close();
                fileHandle = handle;
                markSaved(handle.name);
                return;
            }
        } catch (err) {
            if (isAbortError(err)) return;
            lastError.value = `Could not save: ${err instanceof Error ? err.message : String(err)}`;
            console.error('Map saving error:', err);
            return;
        }

        downloadAmd(document.value);
        markSaved();
    }

    /**
     * Get tile at coordinates
     */
    function getTile(x: number, y: number): TileData | null {
        if (!document.value) return null;
        if (x < 0 || x >= document.value.width) return null;
        if (y < 0 || y >= document.value.height) return null;
        return document.value.tiles[x][y];
    }

    /**
     * Begin a new editing operation (for undo grouping)
     */
    function beginOperation(): void {
        // An operation left open (its end was missed) already changed the map.
        // Dropping it here would make those edits impossible to undo.
        endOperation();

        currentOperation = {
            before: new Map(),
            after: new Map(),
        };
    }

    /**
     * End the current operation and push to undo stack
     */
    function endOperation(): void {
        const operation = currentOperation;
        currentOperation = null;

        if (!operation || operation.before.size === 0) return;

        undoStack.value = [...undoStack.value, operation].slice(-MAX_UNDO_HISTORY);
        redoStack.value = [];
    }

    /**
     * Run edits as a single undo step
     */
    function runOperation(edit: () => void): void {
        beginOperation();
        try {
            edit();
        } finally {
            endOperation();
        }
    }

    /**
     * Set tile at coordinates. Does nothing if the tile already has these values.
     */
    function setTile(x: number, y: number, tile: Partial<TileData>): void {
        const doc = document.value;
        if (!doc) return;
        if (x < 0 || x >= doc.width) return;
        if (y < 0 || y >= doc.height) return;

        const currentTile = doc.tiles[x][y];

        let changed = false;
        for (const field of Object.keys(tile) as Array<keyof TileData>) {
            if (tile[field] !== undefined && tile[field] !== currentTile[field]) {
                changed = true;
                break;
            }
        }
        if (!changed) return;

        // Tiles are replaced, never mutated, so undo snapshots can share them
        const newTile: TileData = { ...currentTile, ...tile };

        if (currentOperation) {
            const key = `${x},${y}`;
            if (!currentOperation.before.has(key)) {
                currentOperation.before.set(key, currentTile);
            }
            currentOperation.after.set(key, newTile);
        }

        doc.tiles[x][y] = newTile;
        doc.dirty = true;
        scheduleDocumentTrigger();
        notifyTileChanged(x, y, newTile);
    }

    /**
     * Set base tile sprite
     */
    function setBaseTile(
        x: number,
        y: number,
        tileSprite: number,
        tileSpriteFrame: number
    ): void {
        setTile(x, y, { tileSprite, tileSpriteFrame });
    }

    /**
     * Set object tile
     */
    function setObject(
        x: number,
        y: number,
        objectSprite: number,
        objectSpriteFrame: number
    ): void {
        setTile(x, y, { objectSprite, objectSpriteFrame });
    }

    /**
     * Clear object at position
     */
    function clearObject(x: number, y: number): void {
        setTile(x, y, { objectSprite: 0, objectSpriteFrame: 0 });
    }

    /**
     * Set tile flags
     */
    function setTileFlags(
        x: number,
        y: number,
        flags: Partial<Record<TileFlagKey, boolean>>
    ): void {
        setTile(x, y, flags);
    }

    /**
     * Write one side of an operation back into the map
     */
    function applySnapshot(snapshot: Map<string, TileData>): void {
        const doc = document.value;
        if (!doc) return;

        for (const [key, tile] of snapshot) {
            const [x, y] = key.split(',').map(Number);
            doc.tiles[x][y] = tile;
            notifyTileChanged(x, y, tile);
        }

        doc.dirty = true;
        scheduleDocumentTrigger();
    }

    /**
     * Undo last operation
     */
    function undo(): void {
        if (!document.value || undoStack.value.length === 0) return;

        const operation = undoStack.value[undoStack.value.length - 1];
        undoStack.value = undoStack.value.slice(0, -1);
        redoStack.value = [...redoStack.value, operation];
        applySnapshot(operation.before);
    }

    /**
     * Redo last undone operation
     */
    function redo(): void {
        if (!document.value || redoStack.value.length === 0) return;

        const operation = redoStack.value[redoStack.value.length - 1];
        redoStack.value = redoStack.value.slice(0, -1);
        undoStack.value = [...undoStack.value, operation];
        applySnapshot(operation.after);
    }

    function clearError(): void {
        lastError.value = null;
    }

    return {
        // State
        document,
        lastError,
        // Computed
        hasMap,
        isDirty,
        canUndo,
        canRedo,
        mapWidth,
        mapHeight,
        filename,
        // Actions
        onTileChanged,
        newMap,
        loadMap,
        openMapWithPicker,
        saveMap,
        getTile,
        beginOperation,
        endOperation,
        runOperation,
        setTile,
        setBaseTile,
        setObject,
        clearObject,
        setTileFlags,
        undo,
        redo,
        clearError,
    };
});
